import { useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, chakra, Flex, Text } from "@chakra-ui/react";
import { useReq } from "@larner.dev/use-req";
import {
  ContactPreferenceEnum,
  UserProfile,
  UserProfileWithContactInfo,
} from "@greenflagdate/shared";
import { Alert } from "../../../components/Alert/Alert";
import { useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInstagram } from "@fortawesome/free-brands-svg-icons";

import { apiReq } from "../../../lib/apiReq";
import { isLoggedIn } from "../../../lib/isLoggedIn";
import { refreshUserState, useStoreData } from "../../../lib/store/store";
import { LoaderScreen } from "../../../components/LoaderScreen/LoaderScreen";
import { Icon } from "../../../components/Icon/Icon";
import { UnmatchConfirmModal } from "../../../components/UnmatchConfirmModal/UnmatchConfirmModal";
import { useMediaBlob } from "../../../lib/mediaHelpers";
import {
  primaryProfileImagePath,
  useMeasuredVariantPath,
} from "../../../lib/profileMedia";
import { GroupMeInfoDialog } from "../../../components/GroupMeInfoDialog/GroupMeInfoDialog";
import { ModalPage } from "../../../components/ModalPage";


function Avatar({
  path,
  size,
  alt,
}: {
  path?: string;
  size: string;
  alt: string;
}) {
  const [setMeasureRef, variantPath] = useMeasuredVariantPath(path);
  const { blob } = useMediaBlob(variantPath ?? "");
  const bg = blob ? `url(${URL.createObjectURL(blob)})` : undefined;
  return (
    <Box
      ref={setMeasureRef}
      aria-label={alt}
      w={size}
      h={size}
      borderRadius="full"
      bg={bg ? "bg.subtle" : "bg.brandSoft"}
      backgroundImage={bg}
      backgroundSize="cover"
      backgroundPosition="center"
      flexShrink={0}
      display="flex"
      alignItems="center"
      justifyContent="center"
      color="fg.brand"
      boxShadow="0 0 12.1px 2px rgba(0,0,0,0.05)"
    >
      {!bg && <Icon name="user-solid" boxSize="50%" />}
    </Box>
  );
}

type ContactKind = "phone" | "email" | "instagram" | "groupme";

type ContactMethod = {
  kind: ContactKind;
  label: string;
  value: string;
  href?: string;
  onClick?: () => void;
};

function BrandGlyph({ kind }: { kind: ContactKind }) {
  if (kind === "phone") {
    return (
      <Flex
        w="36px"
        h="36px"
        borderRadius="sm"
        bg="bg.brand"
        color="fg.onBrand"
        align="center"
        justify="center"
        flexShrink={0}
      >
        <Icon name="chat" boxSize="18px" />
      </Flex>
    );
  }
  if (kind === "email") {
    return (
      <Flex
        w="36px"
        h="36px"
        borderRadius="sm"
        bg="bg.brand"
        color="fg.onBrand"
        align="center"
        justify="center"
        flexShrink={0}
      >
        <Icon name="envelope" boxSize="18px" />
      </Flex>
    );
  }
  if (kind === "instagram") {
    return (
      <Flex
        w="36px"
        h="36px"
        borderRadius="sm"
        bg="linear-gradient(135deg, #F9CE34 0%, #EE2A7B 50%, #6228D7 100%)"
        color="white"
        align="center"
        justify="center"
        flexShrink={0}
      >
        <FontAwesomeIcon icon={faInstagram} fontSize="18px" />
      </Flex>
    );
  }
  return (
    <Flex
      w="36px"
      h="36px"
      borderRadius="sm"
      bg="#00AFF0"
      color="white"
      align="center"
      justify="center"
      flexShrink={0}
      fontSize="xs"
      fontWeight="bold"
      letterSpacing="tight"
    >
      GM
    </Flex>
  );
}

function ContactMethodRow({
  method,
  isLast,
}: {
  method: ContactMethod;
  isLast: boolean;
}) {
  const content = (
    <Flex
      align="center"
      gap="3.5"
      px="4"
      py="3.5"
      bg="bg.canvas"
      borderBottomWidth={isLast ? "0" : "1px"}
      borderBottomColor="border.subtle"
      _hover={{ bg: "bg.subtle" }}
      w="100%"
      textAlign="left"
    >
      <BrandGlyph kind={method.kind} />
      <Box flex="1" minW="0">
        <Flex align="center" gap="1.5" mb="0.5">
          <Text
            fontSize="xs"
            fontWeight="semibold"
            color="fg.muted"
            textTransform="uppercase"
            letterSpacing="wider"
          >
            {method.label}
          </Text>
        </Flex>
        <Text textStyle="body" color="fg.default" truncate>
          {method.value}
        </Text>
      </Box>
      <Flex
        w="36px"
        h="36px"
        borderRadius="full"
        bg="bg.subtle"
        color="fg.default"
        align="center"
        justify="center"
        flexShrink={0}
      >
        <Icon name="chevron-right" boxSize="14px" />
      </Flex>
    </Flex>
  );

  if (method.onClick) {
    return (
      <chakra.button
        type="button"
        onClick={method.onClick}
        display="block"
        w="100%"
        bg="transparent"
        border="0"
        p="0"
        cursor="pointer"
        textAlign="left"
      >
        {content}
      </chakra.button>
    );
  }
  if (method.href) {
    return (
      <chakra.a
        href={method.href}
        target={method.kind === "instagram" ? "_blank" : undefined}
        rel={method.kind === "instagram" ? "noopener noreferrer" : undefined}
        display="block"
        w="100%"
        textDecoration="none"
        color="inherit"
      >
        {content}
      </chakra.a>
    );
  }
  return content;
}

function buildContactMethods(
  profile: UserProfileWithContactInfo,
  openGroupme: () => void,
): ContactMethod[] {
  const order: ContactPreferenceEnum[] = profile.contactPreferences.length
    ? profile.contactPreferences
    : [ContactPreferenceEnum.Groupme];
  const methods: ContactMethod[] = [];
  const seen = new Set<ContactKind>();
  const push = (m: ContactMethod) => {
    if (seen.has(m.kind)) return;
    seen.add(m.kind);
    methods.push(m);
  };
  for (const pref of order) {
    if (pref === ContactPreferenceEnum.Phone && profile.phoneNumber) {
      push({
        kind: "phone",
        label: "Text message",
        value: profile.phoneNumber,
        href: `sms:${profile.phoneNumber}`,
      });
    } else if (pref === ContactPreferenceEnum.Email && profile.email) {
      push({
        kind: "email",
        label: "Email",
        value: profile.email,
        href: `mailto:${profile.email}`,
      });
    } else if (
      pref === ContactPreferenceEnum.Instagram &&
      profile.instagramHandle
    ) {
      push({
        kind: "instagram",
        label: "Instagram",
        value: `@${profile.instagramHandle}`,
        href: `https://www.instagram.com/${profile.instagramHandle}/`,
      });
    } else if (pref === ContactPreferenceEnum.Groupme) {
      push({
        kind: "groupme",
        label: "GroupMe",
        value: "Message via Green Flag GroupMe",
        onClick: openGroupme,
      });
    }
  }
  return methods;
}

export const Conversation = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const storeData = useStoreData();
  const [profileReq, profileReqState] = useReq<
    UserProfile | UserProfileWithContactInfo
  >(apiReq);
  const [showGroupme, setShowGroupme] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [unmatchOpen, setUnmatchOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!menuOpen) return;
    function onDocClick(e: MouseEvent) {
      if (!menuRef.current) return;
      if (!menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [menuOpen]);

  useEffect(() => {
    if (isLoggedIn(storeData) && userId) {
      profileReq.get(`/user/profile/${userId}`);
    }
  }, [profileReq, storeData, userId]);

  const profile = profileReqState.result;
  const isMatch = !!profile && "contactPreferences" in profile;
  const methods = useMemo(
    () =>
      isMatch
        ? buildContactMethods(profile as UserProfileWithContactInfo, () =>
            setShowGroupme(true),
          )
        : [],
    [profile, isMatch],
  );
  const photo = profile ? primaryProfileImagePath(profile) : undefined;
  const name = profile?.firstName ?? "";

  const rightAction = isMatch ? (
    <Box ref={menuRef} position="relative">
      <chakra.button
        type="button"
        onClick={() => setMenuOpen((v) => !v)}
        aria-label="Open menu"
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        w="36px"
        h="36px"
        borderRadius="full"
        bg="transparent"
        border="none"
        color="fg.default"
        display="flex"
        alignItems="center"
        justifyContent="center"
        cursor="pointer"
        _hover={{ bg: "bg.subtle" }}
      >
        <Icon name="ellipsis-vertical" boxSize="18px" />
      </chakra.button>
      {menuOpen && (
        <Box
          role="menu"
          position="absolute"
          right="0"
          top="calc(100% + 4px)"
          minW="160px"
          bg="bg.canvas"
          borderWidth="1px"
          borderColor="border.subtle"
          borderRadius="md"
          boxShadow="0 10px 30px rgba(0,0,0,0.18)"
          overflow="hidden"
          zIndex={50}
        >
          <chakra.button
            type="button"
            role="menuitem"
            onClick={() => {
              setMenuOpen(false);
              setUnmatchOpen(true);
            }}
            w="100%"
            textAlign="left"
            px="4"
            py="3"
            bg="transparent"
            border="none"
            color="fg.danger"
            fontSize="sm"
            fontWeight="medium"
            cursor="pointer"
            _hover={{ bg: "bg.subtle" }}
          >
            Unmatch
          </chakra.button>
        </Box>
      )}
    </Box>
  ) : undefined;

  return (
    <ModalPage
      motionKey="conversation-modal"
      title={name}
      fallback="/matches"
      rightAction={rightAction}
      bodyBg="bg.canvas"
    >
      <Box flex="1" minH="0" w="100%" maxW="40rem" mx="auto">
        {profileReqState.loading && !profile ? (
          <LoaderScreen />
        ) : profileReqState.error ? (
          <Box px="5" py="4">
            <Alert
              title={
                profileReqState.error.code === "PROFILE_NOT_AVAILABLE"
                  ? "You can only view profile info for your matches."
                  : profileReqState.error.message
              }
              intent={
                profileReqState.error.code === "PROFILE_NOT_AVAILABLE"
                  ? undefined
                  : "danger"
              }
            />
          </Box>
        ) : !profile ? null : (
          <>
            <Flex
              direction="column"
              align="center"
              bg="bg.canvas"
              px="5"
              pt="5"
              pb="6"
              borderBottomWidth="1px"
              borderBottomColor="border.subtle"
            >
              <Box position="relative" mb="3">
                <Avatar path={photo} size="88px" alt={name} />
                <Flex
                  position="absolute"
                  right="-4px"
                  bottom="-4px"
                  w="30px"
                  h="30px"
                  borderRadius="full"
                  bg="bg.brand"
                  color="fg.onBrand"
                  align="center"
                  justify="center"
                  border="3px solid"
                  borderColor="bg.canvas"
                >
                  <Icon name="heart-filled" boxSize="13px" />
                </Flex>
              </Box>
              <Text
                textStyle="bodyLg"
                fontWeight="medium"
                color="fg.default"
                letterSpacing="-0.01em"
              >
                {name}
                {profile.age ? `, ${profile.age}` : ""}
              </Text>
              <Button
                textStyle="label"
                mt="3.5"
                h="34px"
                px="4"
                borderRadius="full"
                bg="bg.canvas"
                color="fg.default"
                borderWidth="1px"
                borderColor="border.subtle"
                onClick={() =>
                  navigate(`/profile/${profile.id}`, {
                    state: { back: `/matches/${profile.id}` },
                  })
                }
              >
                View profile
                <Icon name="chevron-right" boxSize="9px" />
              </Button>
            </Flex>

            <Flex
              mx="4"
              mt="4"
              mb="2"
              p="3.5"
              bg="bg.brandTint"
              borderRadius="xl"
              gap="3"
              align="flex-start"
            >
              <Flex
                w="32px"
                h="32px"
                borderRadius="full"
                bg="bg.brand"
                color="fg.onBrand"
                align="center"
                justify="center"
                flexShrink={0}
              >
                <Icon name="bell" boxSize="14px" />
              </Flex>
              <Box minW="0">
                <Text
                  textStyle="body"
                  fontWeight="semibold"
                  color="fg.onTint"
                  mb="0.5"
                >
                  Messaging is coming soon
                </Text>
                <Text
                  textStyle="inputLabel"
                  color="fg.onTint"
                  opacity={0.82}
                  lineHeight="1.45"
                >
                  In the meantime, here's how {name} prefers to be reached.
                </Text>
              </Box>
            </Flex>

            {methods.length > 0 ? (
              <Box
                mx="4"
                mt="2"
                borderRadius="xl"
                overflow="hidden"
                boxShadow="0 0 12.1px 2px rgba(0,0,0,0.05)"
              >
                {methods.map((m, i) => (
                  <ContactMethodRow
                    key={m.kind}
                    method={m}
                    isLast={i === methods.length - 1}
                  />
                ))}
              </Box>
            ) : (
              <Box mx="4" mt="2">
                <Alert title={`${name} hasn't shared contact info yet.`} />
              </Box>
            )}

            <Flex
              direction="column"
              align="center"
              gap="2.5"
              px="5"
              pt="5"
              pb="6"
            >
              <Text
                fontSize="xs"
                color="fg.muted"
                textAlign="center"
                maxW="280px"
                lineHeight="1.5"
              >
                Be respectful — {name} chose to share these.
              </Text>
            </Flex>
          </>
        )}
      </Box>

      <GroupMeInfoDialog
        isShown={showGroupme}
        onCloseComplete={() => setShowGroupme(false)}
      />
      {unmatchOpen && profile && (
        <UnmatchConfirmModal
          userId={profile.id}
          firstName={profile.firstName ?? "this match"}
          onClose={() => setUnmatchOpen(false)}
          onConfirmed={() => {
            setUnmatchOpen(false);
            // Refresh the shared /user/state so any mounted consumer
            // (Matches list, upcoming-spark widgets that render this match)
            // re-reads. We don't await — the Matches page's own
            // /user/matches-page useEffect will re-fire when storeData
            // changes, so we can kick off the refresh and navigate back
            // in parallel.
            void refreshUserState();
            navigate("/matches", { replace: true });
          }}
        />
      )}
    </ModalPage>
  );
};
